import {useOptimisticData} from '@github-ui/optimistic-connection/useOptimisticData'
import {useQuery, useQueryClient} from '@github-ui/react-query'
import type {IssueViewerSecondaryViewServerQueryQuery} from '@github-ui/server-persisted-queries/types/IssueViewerSecondaryViewServerQuery'
import type {SubIssuesListItemNestedSubIssuesServerQueryQuery} from '@github-ui/server-persisted-queries/types/SubIssuesListItemNestedSubIssuesServerQuery'
import {createContext, use, useEffect, useMemo} from 'react'

import {useNestedSubIssuesSubscription} from '../subscriptions/sub-issue-subscription'
import {applyIssueSummaryDelta} from '../utils/optimistic-overlay'
import {nestedSubIssuesQueryFn, nestedSubIssuesQueryKey} from './fetch-nested-sub-issues'
import type {SubIssuesListItemNode} from './sub-issues-list-item-query-types'

type IssueNode = NonNullable<NonNullable<IssueViewerSecondaryViewServerQueryQuery['repository']>['issue']>

// Fields read by the list and header, derived from the allowlisted secondary-view query type.
export type SubIssuesData = Pick<
  IssueNode,
  | 'id'
  | 'databaseId'
  | 'number'
  | 'state'
  | 'stateReason'
  | 'subIssuesConnection'
  | 'subIssuesSummary'
  | 'title'
  | 'titleHTML'
  | 'url'
> & {
  readonly repository: Pick<IssueNode['repository'], 'name' | 'nameWithOwner' | 'visibility' | 'viewerCanPush'> & {
    readonly owner: Pick<IssueNode['repository']['owner'], 'login'>
  }
  readonly parent: Pick<NonNullable<IssueNode['parent']>, 'id'> | null
  readonly subIssues: {
    readonly nodes: ReadonlyArray<SubIssuesListItemNode | null> | null
  }
}

// `null` means there is no provider. A provider may intentionally supply `undefined` while its
// parent query is resolving; consumers must not mount a second query owner during that window.
export const SubIssuesDataContext = createContext<SubIssuesData | undefined | null>(null)

export function useSubIssuesDataContext(): SubIssuesData | undefined | null {
  return use(SubIssuesDataContext)
}

export function useSubIssuesData({
  issue,
  parentIssueId,
  owner,
  repo,
  number,
  initialData,
  initialDataUpdatedAt,
  updatesChannel,
}: {
  issue?: SubIssuesData
  parentIssueId: string
  owner: string
  repo: string
  number: number
  initialData?: SubIssuesListItemNestedSubIssuesServerQueryQuery
  // When the page response that produced `initialData` arrived. Lets a cache entry left behind by
  // an earlier visit be recognised as older than what we now have.
  initialDataUpdatedAt?: number
  updatesChannel?: string | null
}): SubIssuesData | undefined {
  const queryKey = useMemo(() => nestedSubIssuesQueryKey(owner, repo, number), [number, owner, repo])
  useNestedSubIssuesSubscription(updatesChannel, owner, repo, number)
  const {data} = useQuery({
    queryKey,
    queryFn: nestedSubIssuesQueryFn(owner, repo, number),
    enabled: initialData !== undefined,
    initialData,
    initialDataUpdatedAt,
    // The issue's own children came with the page's data, so nothing to fetch here. Expanding a
    // child is a separate query.
    staleTime: Infinity,
  })

  // `initialData` only ever applies to an empty cache entry, and entries outlive the page by a long
  // way. So when the cached rows are older than the page's, and differ from them, go and get the
  // current ones.
  const queryClient = useQueryClient()
  useEffect(() => {
    if (initialData === undefined || initialDataUpdatedAt === undefined) return
    const cached = queryClient.getQueryState(queryKey)
    if (cached?.data === undefined || cached.dataUpdatedAt >= initialDataUpdatedAt) return
    if (JSON.stringify(cached.data) === JSON.stringify(initialData)) return
    void queryClient.invalidateQueries({queryKey})
  }, [initialData, initialDataUpdatedAt, queryClient, queryKey])
  const querySubIssues = data?.repository?.issue?.subIssues
  const issueWithNestedSubIssues = useMemo(
    () =>
      issue && querySubIssues
        ? {
            ...issue,
            subIssues: {nodes: querySubIssues.nodes},
            subIssuesConnection: {...issue.subIssuesConnection, totalCount: querySubIssues.totalCount},
          }
        : issue,
    [issue, querySubIssues],
  )
  const connections = useMemo(() => [{ownerId: parentIssueId, connectionPath: ['subIssues']}], [parentIssueId])
  const targets = useMemo(() => (data ? [{queryKey}] : []), [data, queryKey])

  return useOptimisticData(issueWithNestedSubIssues, {
    connections,
    targets,
    applyEntityFieldValuesDelta: applyIssueSummaryDelta,
  })
}
